import { useEffect, useState } from 'react'
import type { MessageSource } from '../lib/messageSource'
import type { Cart, DisplayMessage } from '../types/display'

/**
 * Subscribes to `source` and keeps the latest message plus the last known
 * cart (which persists underneath non-cart overlay messages).
 *
 * `source` must be referentially stable across renders — memoise it with
 * `useMemo` (or hoist it) at the call site. The subscription is torn down
 * and re-established whenever `source`'s identity changes, so passing a
 * freshly constructed source inline (e.g. `useDisplayMessage(createRtdbSource(...))`)
 * would resubscribe on every render.
 */
export function useDisplayMessage(source: MessageSource): {
  message: DisplayMessage | null
  cart: Cart | null
} {
  const [message, setMessage] = useState<DisplayMessage | null>(null)
  const [cart, setCart] = useState<Cart | null>(null)

  useEffect(() => {
    return source.subscribe((next) => {
      setMessage(next)
      if (next.type === 'cart_update') setCart(next.cart)
    })
  }, [source])

  return { message, cart }
}
